/**
 * Persistenter Schullogo-Cache.
 *
 * Ziel: Der Header soll beim allerersten Render (auch nach einem vollen Reload)
 * synchron wissen, ob die Schule ein Logo hat. Dafuer wird das Ergebnis des
 * `GET /schools/:id/logo`-Calls in localStorage gehalten und beim App-Start
 * (AuthContext) einmalig vorgeladen.
 *
 * Werte-Konvention pro schoolId:
 *   - URL-String  -> Schule hat ein Logo (bereits via resolveAssetUrl aufgeloest)
 *   - ''          -> Schule hat KEIN Logo (negativ gecacht)
 *   - undefined   -> noch nie geladen (Header zeigt vorerst das Otter-Logo)
 */
import api from '../services/api';
import { resolveAssetUrl } from './api';

const STORAGE_PREFIX = 'eachotter_school_logo_';

// Schnellster Layer + Dedup laufender Fetches pro schoolId
const memoryCache = new Map();
const inflight = new Map();

const storageKey = (schoolId) => `${STORAGE_PREFIX}${schoolId}`;

/**
 * Liest den gecachten Logo-Wert SYNCHRON (Memory -> localStorage).
 * @returns {string|undefined} URL, '' (kein Logo) oder undefined (unbekannt)
 */
export const getCachedSchoolLogo = (schoolId) => {
  if (!schoolId) return undefined;

  if (memoryCache.has(schoolId)) {
    return memoryCache.get(schoolId);
  }

  try {
    const stored = localStorage.getItem(storageKey(schoolId));
    if (stored !== null) {
      memoryCache.set(schoolId, stored);
      return stored;
    }
  } catch (error) {
    // localStorage kann in seltenen Faellen (Private Mode) werfen - ignorieren
  }

  return undefined;
};

/**
 * Schreibt einen Logo-Wert in Memory + localStorage.
 * @param {string} value URL oder '' (kein Logo)
 */
export const setCachedSchoolLogo = (schoolId, value) => {
  if (!schoolId) return;
  const normalized = value || '';
  memoryCache.set(schoolId, normalized);
  try {
    localStorage.setItem(storageKey(schoolId), normalized);
  } catch (error) {
    // Schreiben fehlgeschlagen (z.B. Quota) - Memory-Cache reicht fuer die Session
  }
};

/**
 * Laedt das Schullogo vom Backend und legt das Ergebnis im Cache ab.
 * Mehrfach-Aufrufe fuer dieselbe schoolId teilen sich dasselbe Promise.
 *
 * Steht bereits ein definitiver Wert im Cache (URL oder '' = kein Logo), wird
 * dieser OHNE Netzwerk-Aufruf zurueckgegeben. Der Header mountet bei jeder
 * Navigation neu und wuerde sonst jedes Mal /logo erneut abfragen (304). Mit
 * `force = true` (App-Start im AuthContext) wird einmal pro Sitzung revalidiert,
 * damit Logo-Aenderungen weiterhin sichtbar werden.
 * @param {string} schoolId
 * @param {{ force?: boolean }} [options]
 * @returns {Promise<string>} URL oder '' (kein Logo)
 */
export const fetchAndCacheSchoolLogo = (schoolId, { force = false } = {}) => {
  if (!schoolId) return Promise.resolve('');

  if (!force) {
    const cached = getCachedSchoolLogo(schoolId);
    // Definitiver Cache-Treffer (URL oder '' fuer "kein Logo") -> kein Netzwerk.
    if (cached !== undefined) {
      return Promise.resolve(cached);
    }
  }

  if (inflight.has(schoolId)) {
    return inflight.get(schoolId);
  }

  const request = (async () => {
    try {
      const response = await api.get(`/schools/${schoolId}/logo`);
      const logo = response.data?.success ? response.data?.logo : null;
      const value = logo ? resolveAssetUrl(logo) : '';
      setCachedSchoolLogo(schoolId, value);
      return value;
    } catch (error) {
      if (error.response?.status === 404) {
        // Schule hat (noch) kein Logo -> negativ cachen
        setCachedSchoolLogo(schoolId, '');
        return '';
      }
      // Netzwerk-/Serverfehler: NICHT cachen, damit spaeter erneut versucht wird
      console.warn('Schullogo konnte nicht geladen werden:', error.response?.status || error.message);
      return getCachedSchoolLogo(schoolId) || '';
    } finally {
      inflight.delete(schoolId);
    }
  })();

  inflight.set(schoolId, request);
  return request;
};
